import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Menu, MenuButton, MenuItems, MenuItem } from '@headlessui/react';
import {
  UserGroupIcon,
  PlusIcon,
  MagnifyingGlassIcon,
  UserIcon,
  CheckCircleIcon,
  ClockIcon,
  EllipsisVerticalIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { User, RegisterRequest, UserUpdateRequest } from '../types/auth';
import { authAPI, adminAPI } from '../services/api';
import { formatShortDate } from '../utils/timezone';
import UserCreateModal from './UserCreateModal';
import UserEditModal from './UserEditModal';
import Card from './ui/Card';
import Button from './ui/Button';
import StatusBanner from './ui/StatusBanner';
import Modal from './ui/Modal';
import { Textarea } from './ui/FormField';
import toast from 'react-hot-toast';
import { apiErrorMessage } from '../utils/apiError';

interface UserManagementProps {
  className?: string;
}

type TabType = 'users' | 'pending';
type RoleFilter = 'all' | 'member' | 'patient' | 'admin';
type StatusFilter = 'any' | 'active' | 'pending' | 'rejected' | 'inactive';

const ROLE_LABEL: Record<User['role'], string> = { admin: 'Admin', member: 'Clinician', patient: 'Client' };

/** One word for where an account stands, used for the chip and the status filter. */
const statusOf = (u: User): Exclude<StatusFilter, 'any'> => {
  if (u.role === 'member' && u.approval_status === 'pending') return 'pending';
  if (u.role === 'member' && u.approval_status === 'rejected') return 'rejected';
  return u.is_active ? 'active' : 'inactive';
};

const STATUS_CHIP: Record<Exclude<StatusFilter, 'any'>, { label: string; className: string }> = {
  active: { label: 'Active', className: 'bg-clinic-primary/15 text-clinic-primary' },
  pending: { label: 'Pending approval', className: 'bg-clinic-gold/20 text-clinic-gold' },
  rejected: { label: 'Rejected', className: 'bg-clinic-accent/15 text-clinic-accent' },
  inactive: { label: 'Inactive', className: 'bg-clinic-muted/20 text-clinic-muted' },
};

const ROLE_CHIP: Record<User['role'], string> = {
  admin: 'bg-clinic-accent/15 text-clinic-accent',
  member: 'bg-clinic-gold/20 text-clinic-gold',
  patient: 'bg-clinic-primary/15 text-clinic-primary',
};

/** Rejected and inactive accounts can be removed; the server refuses if they have history. */
const canRemove = (u: User) => u.role !== 'admin' && (statusOf(u) === 'rejected' || statusOf(u) === 'inactive');

/**
 * Manage Users. One list with search, role chips and a status filter (so
 * rejected and inactive accounts are one click away instead of buried), a
 * Pending approvals queue, and a per-row menu with every action including
 * Remove for accounts that never did anything.
 */
const UserManagement: React.FC<UserManagementProps> = ({ className = '' }) => {
  const location = useLocation();
  const navigate = useNavigate();

  const activeTab: TabType = location.pathname.includes('/pending') ? 'pending' : 'users';

  const [users, setUsers] = useState<User[]>([]);
  const [pendingClinicians, setPendingClinicians] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [pendingLoading, setPendingLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState<RoleFilter>('all');
  // The old /admin/users/inactive route lands on the inactive filter.
  const [statusFilter, setStatusFilter] = useState<StatusFilter>(location.pathname.includes('/inactive') ? 'inactive' : 'any');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [actionLoading, setActionLoading] = useState<number | null>(null);
  const [rejectTarget, setRejectTarget] = useState<{ user: User; reason: string } | null>(null);
  const [deactivateTarget, setDeactivateTarget] = useState<User | null>(null);
  const [removeTarget, setRemoveTarget] = useState<User | null>(null);
  const [removeError, setRemoveError] = useState<string | null>(null);

  const fetchUsers = useCallback(async (retryCount = 0) => {
    try {
      setLoading(true);
      setError(null);
      setUsers(await authAPI.getAllUsers());
    } catch (err: any) {
      if (retryCount === 0) {            // auth token race on first load
        setTimeout(() => fetchUsers(1), 500);
        return;
      }
      setError(`Failed to fetch users: ${err.response?.data?.detail || err.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchPendingClinicians = useCallback(async () => {
    try {
      setPendingLoading(true);
      setPendingClinicians(await adminAPI.getPendingClinicians());
    } catch (err) {
      console.error('Error fetching pending clinicians:', err);
    } finally {
      setPendingLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
    fetchPendingClinicians();
  }, [fetchUsers, fetchPendingClinicians]);

  // ---- actions ------------------------------------------------------------

  const handleApprove = async (user: User) => {
    setActionLoading(user.id);
    try {
      await adminAPI.approveClinician(user.id);
      toast.success(`${user.full_name} approved`);
      setPendingClinicians(prev => prev.filter(c => c.id !== user.id));
      fetchUsers();
    } catch (err: any) {
      toast.error(apiErrorMessage(err, 'Failed to approve clinician'));
    } finally {
      setActionLoading(null);
    }
  };

  const handleReject = async () => {
    if (!rejectTarget) return;
    setActionLoading(rejectTarget.user.id);
    try {
      await adminAPI.rejectClinician(rejectTarget.user.id, rejectTarget.reason || undefined);
      toast.success(`${rejectTarget.user.full_name} rejected`);
      setPendingClinicians(prev => prev.filter(c => c.id !== rejectTarget.user.id));
      setRejectTarget(null);
      fetchUsers();
    } catch (err: any) {
      toast.error(apiErrorMessage(err, 'Failed to reject clinician'));
    } finally {
      setActionLoading(null);
    }
  };

  const handleCreateUser = async (userData: RegisterRequest) => {
    try {
      setActionLoading(0);
      const newUser = await authAPI.createUser(userData);
      setUsers(prev => [...prev, newUser]);
      setShowCreateModal(false);
      toast.success(`${newUser.full_name} added`);
    } catch (err) {
      setError('Failed to create user. Please try again.');
    } finally {
      setActionLoading(null);
    }
  };

  const handleUpdateUser = async (userId: number, userData: UserUpdateRequest) => {
    try {
      setActionLoading(userId);
      const updated = await authAPI.updateUser(userId, userData);
      setUsers(prev => prev.map(u => (u.id === userId ? updated : u)));
      setEditingUser(null);
      toast.success('Changes saved');
    } catch (err) {
      setError('Failed to update user. Please try again.');
    } finally {
      setActionLoading(null);
    }
  };

  const setActive = async (user: User, active: boolean) => {
    try {
      setActionLoading(user.id);
      const updated = active ? await authAPI.activateUser(user.id) : await authAPI.deactivateUser(user.id);
      setUsers(prev => prev.map(u => (u.id === user.id ? updated : u)));
      toast.success(active ? `${user.full_name} reactivated` : `${user.full_name} deactivated`);
    } catch (err: any) {
      toast.error(apiErrorMessage(err, 'Failed to update user status'));
    } finally {
      setActionLoading(null);
      setDeactivateTarget(null);
    }
  };

  const handleRemove = async () => {
    if (!removeTarget) return;
    try {
      setActionLoading(removeTarget.id);
      setRemoveError(null);
      await authAPI.deleteUser(removeTarget.id);
      setUsers(prev => prev.filter(u => u.id !== removeTarget.id));
      toast.success(`${removeTarget.full_name} removed`);
      setRemoveTarget(null);
    } catch (err: any) {
      setRemoveError(apiErrorMessage(err, 'Failed to remove this account'));
    } finally {
      setActionLoading(null);
    }
  };

  // ---- derived ------------------------------------------------------------

  const filteredUsers = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    return users.filter(u => {
      const matchesSearch = !q
        || u.full_name.toLowerCase().includes(q)
        || u.email.toLowerCase().includes(q)
        || (u.specialty || '').toLowerCase().includes(q)
        || (u.phone || '').toLowerCase().includes(q);
      const matchesRole = roleFilter === 'all' || u.role === roleFilter;
      const matchesStatus = statusFilter === 'any' || statusOf(u) === statusFilter;
      return matchesSearch && matchesRole && matchesStatus;
    });
  }, [users, searchTerm, roleFilter, statusFilter]);

  const counts = useMemo(() => ({
    all: users.length,
    member: users.filter(u => u.role === 'member').length,
    patient: users.filter(u => u.role === 'patient').length,
    admin: users.filter(u => u.role === 'admin').length,
    rejected: users.filter(u => statusOf(u) === 'rejected').length,
    inactive: users.filter(u => statusOf(u) === 'inactive').length,
  }), [users]);

  const clearFilters = () => { setSearchTerm(''); setRoleFilter('all'); setStatusFilter('any'); };
  const filtersActive = searchTerm !== '' || roleFilter !== 'all' || statusFilter !== 'any';

  // ---- pieces -------------------------------------------------------------

  const statusChip = (u: User) => {
    const s = STATUS_CHIP[statusOf(u)];
    return <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full whitespace-nowrap ${s.className}`}>{s.label}</span>;
  };

  const roleChip = (u: User) => (
    <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${ROLE_CHIP[u.role]}`}>{ROLE_LABEL[u.role]}</span>
  );

  const detailsOf = (u: User): string => {
    if (u.role === 'member') return [u.specialty, u.license_number && `Lic. ${u.license_number}`].filter(Boolean).join(' · ');
    if (u.role === 'patient') return [u.phone, u.clinician_name && `with ${u.clinician_name}`].filter(Boolean).join(' · ');
    return '';
  };

  const rowMenu = (u: User) => {
    const s = statusOf(u);
    const busy = actionLoading === u.id;
    const item = (label: string, onClick: () => void, opts: { danger?: boolean; disabled?: boolean } = {}) => (
      <MenuItem key={label} disabled={opts.disabled}>
        {(bag: any) => (
          <button
            type="button"
            onClick={onClick}
            disabled={opts.disabled}
            className={`w-full text-left px-4 py-2 text-sm disabled:opacity-40 disabled:cursor-not-allowed ${
              opts.danger ? 'text-clinic-accent font-medium' : 'text-clinic-dark'
            } ${(bag.focus || bag.active) && !opts.disabled ? (opts.danger ? 'bg-clinic-accent/10' : 'bg-clinic-surface-sunken') : ''}`}
          >
            {label}
          </button>
        )}
      </MenuItem>
    );
    return (
      <Menu as="div" className="relative inline-block text-left">
        <MenuButton
          aria-label={`Actions for ${u.full_name}`}
          disabled={busy}
          className="inline-flex items-center justify-center h-9 w-9 rounded-lg text-clinic-muted hover:bg-clinic-surface-sunken hover:text-clinic-primary focus:outline-none focus:ring-2 focus:ring-clinic-primary transition-colors disabled:opacity-50"
        >
          <EllipsisVerticalIcon className="h-5 w-5" />
        </MenuButton>
        <MenuItems anchor="bottom end" className="w-52 mt-1 rounded-lg bg-white shadow-clinic-lg border border-clinic-primary-lighter py-1 z-50 focus:outline-none">
          {item('Edit details', () => setEditingUser(u))}
          {s === 'pending' && item('Approve', () => handleApprove(u))}
          {s === 'pending' && item('Reject…', () => setRejectTarget({ user: u, reason: '' }))}
          {s === 'active' && item('Deactivate…', () => setDeactivateTarget(u))}
          {s === 'inactive' && item('Reactivate', () => setActive(u, true))}
          {canRemove(u) && <div className="my-1 border-t border-clinic-primary-lighter" />}
          {canRemove(u) && item('Remove account…', () => { setRemoveError(null); setRemoveTarget(u); }, { danger: true })}
        </MenuItems>
      </Menu>
    );
  };

  const emptyState = (title: string, body: string, action?: React.ReactNode) => (
    <div className="text-center py-12">
      <UserGroupIcon className="mx-auto h-10 w-10 text-clinic-muted" />
      <p className="mt-2 text-sm font-medium text-clinic-dark">{title}</p>
      <p className="mt-1 text-sm text-clinic-muted">{body}</p>
      {action && <div className="mt-3">{action}</div>}
    </div>
  );

  if (loading && users.length === 0) {
    return (
      <div className={className}>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-clinic-primary" />
        </div>
      </div>
    );
  }

  return (
    <div className={className}>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-clinic-dark font-body">Manage Users</h1>
        {activeTab === 'users' && (
          <Button onClick={() => setShowCreateModal(true)} leftIcon={<PlusIcon className="h-5 w-5" />}>Add user</Button>
        )}
      </div>

      {/* Tabs */}
      <div className="border-b border-clinic-primary-lighter mb-6">
        <nav className="-mb-px flex space-x-8" role="tablist">
          <button role="tab" aria-selected={activeTab === 'users'} onClick={() => navigate('/admin/users')}
            className={`pb-3 px-1 border-b-2 font-medium text-sm inline-flex items-center transition-colors ${
              activeTab === 'users' ? 'border-clinic-primary text-clinic-primary' : 'border-transparent text-clinic-muted hover:text-clinic-dark'}`}>
            <UserGroupIcon className="h-5 w-5 mr-2" />All users
            <span className="ml-2 text-xs text-clinic-muted">{counts.all}</span>
          </button>
          <button role="tab" aria-selected={activeTab === 'pending'} onClick={() => navigate('/admin/users/pending')}
            className={`pb-3 px-1 border-b-2 font-medium text-sm inline-flex items-center transition-colors ${
              activeTab === 'pending' ? 'border-clinic-primary text-clinic-primary' : 'border-transparent text-clinic-muted hover:text-clinic-dark'}`}>
            <ClockIcon className="h-5 w-5 mr-2" />Pending approvals
            {pendingClinicians.length > 0 && (
              <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-clinic-gold/20 text-clinic-gold">
                {pendingClinicians.length}
              </span>
            )}
          </button>
        </nav>
      </div>

      {error && (
        <div className="mb-6">
          <StatusBanner variant="error" title="Error" message={error} dismissible onDismiss={() => setError(null)} />
          <div className="mt-2"><Button size="sm" variant="secondary" onClick={() => { setError(null); fetchUsers(); }}>Retry</Button></div>
        </div>
      )}

      {/* ---------------- All users ---------------- */}
      {activeTab === 'users' && (
        <>
          {/* Toolbar: search, role chips, status */}
          <Card className="p-4 mb-4">
            <div className="flex flex-col lg:flex-row lg:items-center gap-3">
              <div className="flex-1 relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-clinic-muted" />
                <input
                  type="search"
                  placeholder="Search name, email, specialty or phone"
                  aria-label="Search users"
                  className="w-full pl-10 pr-4 py-2 border border-clinic-primary-light rounded-lg focus:ring-2 focus:ring-clinic-primary/30 focus:border-clinic-primary bg-white"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <div className="flex rounded-lg bg-clinic-surface-sunken p-1" role="tablist" aria-label="Role">
                {([['all', 'All'], ['member', 'Clinicians'], ['patient', 'Clients'], ['admin', 'Admins']] as [RoleFilter, string][]).map(([v, label]) => (
                  <button key={v} type="button" role="tab" aria-selected={roleFilter === v} onClick={() => setRoleFilter(v)}
                    className={`px-3 py-1.5 text-sm font-medium rounded-md whitespace-nowrap transition-colors ${
                      roleFilter === v ? 'bg-white text-clinic-dark shadow-clinic-sm' : 'text-clinic-muted hover:text-clinic-dark'}`}>
                    {label} <span className="text-xs text-clinic-muted">{counts[v]}</span>
                  </button>
                ))}
              </div>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
                aria-label="Status"
                className="px-3 py-2 border border-clinic-primary-light rounded-lg focus:ring-2 focus:ring-clinic-primary/30 focus:border-clinic-primary bg-white text-sm"
              >
                <option value="any">Any status</option>
                <option value="active">Active</option>
                <option value="pending">Pending approval</option>
                <option value="rejected">Rejected ({counts.rejected})</option>
                <option value="inactive">Inactive ({counts.inactive})</option>
              </select>
            </div>
          </Card>

          {(counts.rejected > 0 && statusFilter === 'any' && !filtersActive) && (
            <p className="text-xs text-clinic-muted mb-3">
              {counts.rejected} rejected sign-up{counts.rejected === 1 ? '' : 's'} in this list.{' '}
              <button type="button" className="text-clinic-primary hover:text-clinic-primary-dark" onClick={() => setStatusFilter('rejected')}>
                Show only rejected
              </button>{' '}
              to review or remove them.
            </p>
          )}

          <Card padding="none">
            <div className="px-5 py-3 border-b border-clinic-primary-lighter flex items-center justify-between">
              <h2 className="text-sm font-semibold text-clinic-dark">
                {filteredUsers.length} {filteredUsers.length === 1 ? 'user' : 'users'}
                {filtersActive && <span className="font-normal text-clinic-muted"> matching</span>}
              </h2>
              {filtersActive && (
                <button type="button" onClick={clearFilters} className="text-xs text-clinic-primary hover:text-clinic-primary-dark">Clear filters</button>
              )}
            </div>

            {filteredUsers.length === 0 ? (
              emptyState(
                filtersActive ? 'No users match' : 'No users yet',
                filtersActive ? 'Try a different search or filter.' : 'Add the first user to get started.',
                filtersActive ? <Button size="sm" variant="secondary" onClick={clearFilters}>Clear filters</Button> : undefined,
              )
            ) : (
              <>
                {/* Desktop: table. Sticky first column keeps names visible while the rest scrolls sideways. */}
                <div className="hidden md:block overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead className="bg-clinic-cream/40 text-left text-xs font-medium text-clinic-muted uppercase tracking-wider">
                      <tr>
                        <th className="sticky left-0 bg-clinic-cream/40 backdrop-blur px-5 py-3 min-w-[16rem]">User</th>
                        <th className="px-4 py-3">Role</th>
                        <th className="px-4 py-3">Status</th>
                        <th className="px-4 py-3 min-w-[14rem]">Details</th>
                        <th className="px-4 py-3 whitespace-nowrap">Joined</th>
                        <th className="px-4 py-3 text-right sticky right-0 bg-clinic-cream/40 backdrop-blur"><span className="sr-only">Actions</span></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-clinic-primary-lighter">
                      {filteredUsers.map((u) => {
                        const s = statusOf(u);
                        const dim = s === 'inactive' || s === 'rejected';
                        return (
                          <tr key={u.id} className="group hover:bg-clinic-cream/20 transition-colors" data-testid={`user-row-${u.id}`}>
                            <td className={`sticky left-0 bg-white group-hover:bg-clinic-cream/20 px-5 py-3 ${dim ? 'opacity-70' : ''}`}>
                              <div className="flex items-center gap-3 min-w-0">
                                {u.profile_photo_url ? (
                                  <img src={u.profile_photo_url} alt="" className="h-9 w-9 rounded-full object-cover flex-shrink-0" />
                                ) : (
                                  <div className="h-9 w-9 rounded-full bg-clinic-cream flex items-center justify-center flex-shrink-0">
                                    <UserIcon className="h-4 w-4 text-clinic-muted" />
                                  </div>
                                )}
                                <div className="min-w-0">
                                  <div className="font-medium text-clinic-dark truncate">{u.full_name}</div>
                                  <div className="text-clinic-muted truncate">{u.email}</div>
                                </div>
                              </div>
                            </td>
                            <td className="px-4 py-3">{roleChip(u)}</td>
                            <td className="px-4 py-3">
                              {statusChip(u)}
                              {s === 'rejected' && u.approval_status_reason && (
                                <div className="text-xs text-clinic-muted mt-1 max-w-[14rem] truncate" title={u.approval_status_reason}>{u.approval_status_reason}</div>
                              )}
                            </td>
                            <td className="px-4 py-3 text-clinic-muted">{detailsOf(u) || <span className="text-clinic-muted/60">—</span>}</td>
                            <td className="px-4 py-3 text-clinic-muted whitespace-nowrap">{formatShortDate(u.created_at)}</td>
                            <td className="px-3 py-2 text-right sticky right-0 bg-white group-hover:bg-clinic-cream/20">
                              {s === 'pending' ? (
                                <div className="flex justify-end gap-2">
                                  <Button size="sm" onClick={() => handleApprove(u)} isLoading={actionLoading === u.id} disabled={actionLoading === u.id}>Approve</Button>
                                  {rowMenu(u)}
                                </div>
                              ) : rowMenu(u)}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Mobile: cards, no sideways scrolling needed. */}
                <ul className="md:hidden divide-y divide-clinic-primary-lighter">
                  {filteredUsers.map((u) => (
                    <li key={u.id} className="px-4 py-3 flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="font-medium text-clinic-dark truncate">{u.full_name}</p>
                        <p className="text-sm text-clinic-muted truncate">{u.email}</p>
                        <div className="flex flex-wrap items-center gap-1.5 mt-1.5">{roleChip(u)}{statusChip(u)}</div>
                        {detailsOf(u) && <p className="text-xs text-clinic-muted mt-1">{detailsOf(u)}</p>}
                      </div>
                      {rowMenu(u)}
                    </li>
                  ))}
                </ul>
              </>
            )}
          </Card>
        </>
      )}

      {/* ---------------- Pending approvals ---------------- */}
      {activeTab === 'pending' && (
        <Card padding="none">
          {pendingLoading ? (
            <div className="flex items-center justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-clinic-primary" /></div>
          ) : pendingClinicians.length === 0 ? (
            <div className="text-center py-12">
              <CheckCircleIcon className="mx-auto h-10 w-10 text-clinic-primary" />
              <p className="mt-2 text-sm font-medium text-clinic-dark">No pending approvals</p>
              <p className="mt-1 text-sm text-clinic-muted">
                Every clinician sign-up has been processed.{' '}
                {counts.rejected > 0 && (
                  <button type="button" className="text-clinic-primary hover:text-clinic-primary-dark"
                    onClick={() => { setStatusFilter('rejected'); navigate('/admin/users'); }}>
                    See {counts.rejected} rejected
                  </button>
                )}
              </p>
            </div>
          ) : (
            <ul className="divide-y divide-clinic-primary-lighter">
              {pendingClinicians.map((c) => (
                <li key={c.id} className="px-5 py-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid={`pending-${c.id}`}>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-clinic-dark">{c.first_name} {c.last_name}</p>
                    <p className="text-sm text-clinic-muted truncate">{c.email}</p>
                    <p className="text-xs text-clinic-muted mt-1">
                      {[c.specialty, c.license_number && `Lic. ${c.license_number}`, `applied ${formatShortDate(c.created_at)}`].filter(Boolean).join(' · ')}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button size="sm" variant="secondary" onClick={() => setRejectTarget({ user: c, reason: '' })} disabled={actionLoading === c.id}>Reject</Button>
                    <Button size="sm" onClick={() => handleApprove(c)} isLoading={actionLoading === c.id} disabled={actionLoading === c.id}>Approve</Button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Card>
      )}

      {/* Reject */}
      <Modal
        isOpen={rejectTarget !== null}
        onClose={() => setRejectTarget(null)}
        title={rejectTarget ? `Reject ${rejectTarget.user.full_name}?` : 'Reject'}
        size="sm"
        footer={(
          <>
            <Button variant="ghost" onClick={() => setRejectTarget(null)}>Keep pending</Button>
            <Button variant="danger" onClick={handleReject} isLoading={actionLoading !== null}>Reject</Button>
          </>
        )}
      >
        <p className="text-sm text-clinic-muted mb-3">They'll get an email saying their registration wasn't approved. You can remove the account afterwards.</p>
        <label htmlFor="reject-reason" className="block text-sm font-medium text-clinic-dark mb-1">Reason (optional, internal)</label>
        <Textarea id="reject-reason" rows={3} placeholder="e.g. spam sign-up, not a registered practitioner"
          value={rejectTarget?.reason || ''} onChange={(e) => setRejectTarget(prev => prev && ({ ...prev, reason: e.target.value }))} />
      </Modal>

      {/* Deactivate */}
      <Modal
        isOpen={deactivateTarget !== null}
        onClose={() => setDeactivateTarget(null)}
        title={deactivateTarget ? `Deactivate ${deactivateTarget.full_name}?` : 'Deactivate'}
        size="sm"
        footer={(
          <>
            <Button variant="ghost" onClick={() => setDeactivateTarget(null)}>Cancel</Button>
            <Button variant="danger" onClick={() => deactivateTarget && setActive(deactivateTarget, false)} isLoading={actionLoading !== null}>Deactivate</Button>
          </>
        )}
      >
        <ul className="list-disc list-inside text-sm text-clinic-muted space-y-1">
          <li>They can't log in.</li>
          {deactivateTarget?.role === 'member' && <><li>Hidden from the clinician directory.</li><li>No new bookings with them.</li></>}
        </ul>
        <p className="text-sm text-clinic-muted mt-3">Existing bookings and history are kept. You can reactivate any time.</p>
      </Modal>

      {/* Remove */}
      <Modal
        isOpen={removeTarget !== null}
        onClose={() => { if (actionLoading === null) setRemoveTarget(null); }}
        title={removeTarget ? `Remove ${removeTarget.full_name}?` : 'Remove'}
        size="sm"
        footer={(
          <>
            <Button variant="ghost" onClick={() => setRemoveTarget(null)} disabled={actionLoading !== null}>Keep</Button>
            <Button variant="danger" onClick={handleRemove} isLoading={actionLoading !== null} disabled={actionLoading !== null}>Remove permanently</Button>
          </>
        )}
      >
        {removeError && (
          <div className="mb-3"><StatusBanner variant="warning" title="Can't remove" message={removeError} /></div>
        )}
        <p className="text-sm text-clinic-dark flex items-start gap-2">
          <ExclamationTriangleIcon className="h-5 w-5 text-clinic-accent flex-shrink-0" />
          <span>
            This deletes the account and its sign-up details for good. It only works for accounts with no bookings or billing
            history, which is what a rejected or abandoned sign-up looks like. Anything with history should be deactivated instead.
          </span>
        </p>
      </Modal>

      {showCreateModal && (
        <UserCreateModal onClose={() => setShowCreateModal(false)} onSave={handleCreateUser} loading={actionLoading === 0} />
      )}
      {editingUser && (
        <UserEditModal user={editingUser} onClose={() => setEditingUser(null)}
          onSave={(userData) => handleUpdateUser(editingUser.id, userData)} loading={actionLoading === editingUser.id} />
      )}
    </div>
  );
};

export default UserManagement;
